import { useWatch, FieldPath } from 'react-hook-form'

export type ArrayInputContentProps = { source?: string }

/**
 * Extracts array indices from the result of a `getSource()` function
 *
 * #ra4Migration this function is now broken since sources when expressed as strings
 * are in dot notation (users.1.name) and not square bracket notation (users[1].name)
 * FIXME!!
 *
 * @example
 * getIndicesFromGetSourceString('pricing[0].pricingRules[2].') // [0,2]
 *
 * @param s `getSource('')`  result string
 */
export const getIndicesFromGetSourceString = (s: string): number[] =>
  s.match(/([0-9]+)/g)?.map((e) => Number(e))!

const kGetSource =
  <T extends object>(sourceRootWithIndex: string) =>
  (fieldSource: FieldPath<T>) =>
    `${sourceRootWithIndex}.${fieldSource}`

const getLastSourceIndex = (source: string) => {
  const indexes = getIndicesFromGetSourceString(source!)
  return indexes[indexes.length - 1]
}

/**
 *
 * @param source the source passed down from the array input to the FC
 * @returns object containing the getSource function that works exactly like the one of the formDataConsumer,
 * the scopedFormData of that context
 * and the index of that scoped form data
 */
export const useKArrayInput = <TScopedFormData extends object>(
  source?: string
) => {
  if (!source) {
    throw new Error(
      'ERROR!! You are trying to use the `useKArrayInput` hook without passing a defined source to it'
    )
  }

  return {
    scopedFormData: useWatch({ name: source }) as TScopedFormData,
    kGetSource: kGetSource<TScopedFormData>(source),
    index: getLastSourceIndex(source),
  }
}
